// src/composables/useIdleTimer.js
//
// Cross-tab idle detector. Tracks user activity (mouse, keyboard, scroll, touch,
// focus) and writes the latest activity timestamp to localStorage so every tab
// shares the same "globally idle" view. When the global idle window exceeds the
// configured threshold, fires a warning; if the warning lapses, signs the user
// out in every tab (Firebase auth syncs across tabs via IndexedDB, so a single
// signOut() fires onAuthStateChanged everywhere).
//
// Singleton: module-scoped state so App.vue (lifecycle owner) and the warning
// modal (consumer) share one timer + one warning ref.
//
// Config is driven by authConfigStore (globalSettings/authConfig) with sensible
// defaults. Timer is a no-op when disabled or when no user is signed in.

import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/userStore'
import { useAuthConfigStore } from '@/stores/authConfigStore'

const LS_KEY = 'gse_lastActivity'
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
  'focus',
]
const ACTIVITY_WRITE_THROTTLE_MS = 30_000
const CHECK_INTERVAL_MS = 15_000

// Module-scoped singleton state
const warningActive = ref(false)
const secondsUntilLogout = ref(0)

let initialized = false
let isRunning = false
let signOutScheduled = false
let lastLocalWrite = 0
let checkTimer = null
let userStore = null
let authConfig = null
let configRefs = null

function now() {
  return Date.now()
}

function readLastActivity() {
  try {
    const raw = localStorage.getItem(LS_KEY)
    const n = raw ? Number(raw) : NaN
    return Number.isFinite(n) ? n : now()
  } catch {
    return now()
  }
}

function writeLastActivity(ts) {
  try {
    localStorage.setItem(LS_KEY, String(ts))
  } catch {
    /* quota / private mode */
  }
}

function markActivity() {
  const ts = now()
  if (ts - lastLocalWrite < ACTIVITY_WRITE_THROTTLE_MS) return
  lastLocalWrite = ts
  writeLastActivity(ts)
}

function onStorage(event) {
  if (event.key !== LS_KEY) return
  if (warningActive.value) {
    const idle = now() - readLastActivity()
    const timeout = configRefs.idleTimeoutMs.value
    const warn = configRefs.warningMs.value
    if (idle < timeout - warn) {
      warningActive.value = false
      secondsUntilLogout.value = 0
    }
  }
}

async function performSignOut() {
  if (signOutScheduled) return
  signOutScheduled = true
  warningActive.value = false
  try {
    await userStore.signOutUser()
  } catch (err) {
    console.error('[useIdleTimer] signOut error:', err)
    // Release the latch — a failed signOut (transient network at the wrong
    // moment) must not permanently disable idle auto-logout for the session
    // (bugReports/4YBwERm3xlf0omDMUN7b). The user is still signed in and
    // still idle, so the next tick retries; start() remains the reset for
    // the genuine signed-out→in transition.
    signOutScheduled = false
  }
}

function tick() {
  if (!configRefs.enabled.value || !userStore.user) {
    warningActive.value = false
    return
  }
  const idle = now() - readLastActivity()
  const timeout = configRefs.idleTimeoutMs.value
  const warn = configRefs.warningMs.value

  if (idle >= timeout) {
    performSignOut()
    return
  }

  if (warn > 0 && idle >= timeout - warn) {
    warningActive.value = true
    secondsUntilLogout.value = Math.max(0, Math.ceil((timeout - idle) / 1000))
  } else {
    warningActive.value = false
    secondsUntilLogout.value = 0
  }
}

function start() {
  if (isRunning) return
  isRunning = true
  signOutScheduled = false
  writeLastActivity(now())
  lastLocalWrite = now()

  ACTIVITY_EVENTS.forEach(ev => {
    window.addEventListener(ev, markActivity, { passive: true })
  })
  window.addEventListener('storage', onStorage)

  checkTimer = setInterval(tick, CHECK_INTERVAL_MS)
  tick()
}

function stop() {
  if (!isRunning) return
  isRunning = false

  ACTIVITY_EVENTS.forEach(ev => {
    window.removeEventListener(ev, markActivity)
  })
  window.removeEventListener('storage', onStorage)

  if (checkTimer) {
    clearInterval(checkTimer)
    checkTimer = null
  }
  warningActive.value = false
  secondsUntilLogout.value = 0
}

function stayLoggedIn() {
  writeLastActivity(now())
  lastLocalWrite = now()
  warningActive.value = false
  secondsUntilLogout.value = 0
}

function ensureInit() {
  if (initialized) return
  initialized = true
  userStore = useUserStore()
  authConfig = useAuthConfigStore()
  configRefs = storeToRefs(authConfig)

  const shouldRun = computed(() => configRefs.enabled.value && !!userStore.user)
  watch(
    shouldRun,
    run => {
      if (run) start()
      else stop()
    },
    { immediate: true }
  )
}

export function useIdleTimer() {
  ensureInit()
  return {
    warningActive,
    secondsUntilLogout,
    stayLoggedIn,
    signOutNow: performSignOut,
  }
}
